/**
 * The run screen — everything that was the whole game up to stage 3, now one
 * screen among several (doc 11 §Screen map). It owns the sim, the world views,
 * the camera and the HUD; the shell owns the canvas and the save file.
 *
 * The meta enters here at exactly two seams: the sim is built with the
 * profile's `RunModifiers`, and every cleared shoal banks its Pearls and tower
 * XP on the spot (doc 08) — followed immediately by a run snapshot, so payout
 * and save are one step and nothing can ever be paid twice.
 */

import { L, S } from '../content/strings.ts';
import { Container, Sprite } from 'pixi.js';
import { audio } from '../audio/engine.ts';
import abilitiesJson from '../content/abilities.json' with { type: 'json' };
import { REGISTRY_DEFS } from '../art/registry.ts';
import { effectsOf } from '../meta/effects.ts';
import { Camera, gridBounds, ZOOM_LEVELS } from '../render/camera.ts';
import { blockCentre, screenToTile, TH, TW } from '../render/iso.ts';
import { DEPTHS, DEPTH_IDS, OUTLINE_HOVER, SEMANTIC, type DepthPalette } from '../render/palette.ts';
import { DynamicSurface, surfaceToTexture } from '../render/pipeline.ts';
import { isoDiamondRim } from '../render/primitives.ts';
import { hex, mix, PixelSurface } from '../render/surface.ts';
import { DT, PANIC_TAX } from '../sim/const.ts';
import { PLACEABLE, placeableDef, towerStats } from '../sim/defs.ts';
import { Sim } from '../sim/sim.ts';
import type { AbilityId, Command, RimShoal, Scenario, SimEvent, Structure } from '../sim/types.ts';
import {
  abilityHit,
  drawHud,
  hotbarHit,
  hudLayout,
  speedHit,
  upgradeHit,
  type Banner,
  type HudLayout,
} from '../ui/hud.ts';
import { drawConfirm } from '../ui/menu.ts';
import { g } from '../ui/theme.ts';
import type { Rect } from '../ui/widgets.ts';
import type { App, Screen } from './app.ts';
import { Alerts } from './alerts.ts';
import { Ambience } from './ambience.ts';
import { DamageNumbers } from './damagenumbers.ts';
import { EffectsView } from './effects.ts';
import { Juice, type FlashChoice } from './juice.ts';
import { RunLedger, type RunResult } from './ledger.ts';
import { OverlayView } from './overlays.ts';
import { bakeGround } from './ground.ts';
import { BUILD_ORDER, HOTBAR } from './hotbar.ts';
import { buildHudState } from './hudstate.ts';
import { Minimap } from './minimap.ts';
import { PROPERTIES, propertiesOf } from './properties.ts';
import { NudgeView } from './nudgeview.ts';
import { RimView } from './rimview.ts';
import { handleEvents, watchCore } from './runevents.ts';
import { playEvents } from './sound.ts';
import { Tutor } from './tutor.ts';
import { WorldView } from './worldview.ts';

/** Doc 17 §2: how close to the edge the pointer has to get to pan, in CSS px. */
const EDGE_MARGIN = 8;
/** Doc 17 §6 toast motion, in ms. */
const TOAST_IN_MS = 140;
const TOAST_OUT_MS = 120;

/** The line each depth opens on. One joke, one warning, one per rung. */
const OPENING: Record<number, string> = {
  1: S.run.sixShoalsCircle,
  2: S.run.threeEdgesNow,
  3: S.run.nothingDownHere,
};

export type { RunResult };

export interface RunOptions {
  scenario: Scenario;
  seed: number;
  /** Resume a saved run instead of starting a fresh colony (doc 12 §Saves). */
  snapshot?: string;
  /** Run over, or the player walked out of it (null result = saved and left). */
  onExit(result: RunResult | null): void;
}

export class RunScreen implements Screen {
  readonly sim: Sim;
  private readonly app: App;
  private readonly opts: RunOptions;
  private readonly depth: DepthPalette;

  private readonly padLayer = new Container();
  private readonly bodyLayer = new Container();
  private readonly fxLayer = new Container();
  private readonly rimLayer = new Container();
  /** Marine snow rides in front of everything, including the rim (doc 10). */
  private readonly snowLayer = new Container();
  private readonly barLayer = new Container();
  private readonly minimapView: DynamicSurface;
  private readonly water: DynamicSurface;
  readonly world: WorldView;
  private readonly rim: RimView;
  readonly fx: EffectsView;
  private readonly minimap: Minimap;
  private readonly camera: Camera;
  private readonly coreCentre: { x: number; y: number };

  private selectedStructId = -1;
  private selectedShoalId: string | null = null;
  private hoverStructId = -1;
  private tool: string | null = null;
  /** Which ability is armed for a click, if any (doc 07). */
  private aiming: AbilityId | null = null;
  private hotbarHover = -1;
  private abilityHover = -1;
  private paused: boolean;
  private fastForward = false;
  private hudDirty = true;
  private lastLayout: HudLayout;
  private banner: { title: string; sub: string; start: number; hold: boolean } | null = null;
  private toast: { text: string; until: number; since: number } | null = null;
  private readonly keys = new Set<string>();
  private dragging = false;
  private lastPointer = { x: 0, y: 0 };
  private pointer = { x: -1, y: -1 };
  private acc = 0;
  private lastHud = 0;
  private exitMenu: [Rect, Rect] | null = null;
  private exitFocus: 0 | 1 = 0;
  private finished = false;
  /** Structure awaiting a second X, when confirm-before-sell is on. */
  private sellPending = -1;
  private shaking = false;
  private lastDry = 0;

  readonly ledger: RunLedger;
  readonly juice = new Juice();
  readonly numbers = new DamageNumbers();
  readonly alerts = new Alerts();
  private readonly ambience: Ambience;
  /** Last seen Core HP — the difference is what a Core alert is made of. */
  private coreHp = Infinity;
  private readonly exactCounts: boolean;
  private readonly alwaysRanges: boolean;
  /** Property cards shown when there is no profile to remember them (harness). */
  private readonly propertiesSeen: string[] = [];

  private readonly overlays: OverlayView;

  /** Doc 19: armed only on the first run of a colony, and only on D1. */
  private readonly nudges: NudgeView;

  constructor(app: App, opts: RunOptions) {
    this.app = app;
    this.opts = opts;
    // the depth palette IS the depth: THE GLOOM is not THE SHELF with worse fish
    this.depth = DEPTHS[DEPTH_IDS[Math.max(0, Math.min(2, opts.scenario.depth - 1))]!]!;
    const scenario: Scenario = { ...opts.scenario, modifiers: app.modifiers(opts.scenario.depth) };
    this.sim = opts.snapshot ? Sim.restore(scenario, opts.snapshot) : new Sim(scenario, opts.seed);
    this.ledger = new RunLedger(app, this.sim);
    const eff = app.profile ? effectsOf(app.profile.owned) : null;
    this.exactCounts = eff?.shoalCounts ?? false;
    this.alwaysRanges = eff?.rangeOverlay ?? false;

    // doc 10: the depth's own water, behind everything, in screen space — a
    // zoomed-out view should show ocean past the map's edge, not the void.
    this.water = new DynamicSurface(1, 1);
    app.layers.back.addChild(this.water.sprite);
    this.paintWater();

    const size = scenario.map.size;
    const ground = new Sprite(surfaceToTexture(bakeGround(this.sim.grid, scenario.map, this.depth)));
    ground.x = -(size - 1) * TW - TW;
    ground.y = -TH;
    this.bodyLayer.sortableChildren = true;
    app.stage.addChild(ground, this.padLayer, this.bodyLayer, this.fxLayer, this.rimLayer, this.snowLayer);

    this.world = new WorldView(
      () => this.sim,
      this.padLayer,
      this.bodyLayer,
      app.cache,
      this.depth,
      () => this.juice.flash,
    );
    this.rim = new RimView(() => this.sim, this.rimLayer, app.cache, this.depth, size);
    this.fx = new EffectsView(this.fxLayer, app.cache, this.depth, () => this.juice.flash);
    // doc 13 §F stage-6: things that move down there and are not trying to kill
    // you. Pure presentation — the sim has never heard of any of it.
    this.ambience = new Ambience(this.sim.grid, size, this.depth, app.cache, this.bodyLayer, this.snowLayer);

    this.camera = new Camera(gridBounds(size, size));
    this.coreCentre = blockCentre(scenario.map.core.i, scenario.map.core.j, 3, 3);
    this.camera.centerOn(this.coreCentre.x, this.coreCentre.y, app.viewport);

    this.minimap = new Minimap(this.sim.grid, scenario.map, this.depth);
    this.minimapView = new DynamicSurface(this.minimap.surface.width, this.minimap.surface.height);
    app.layers.ui.addChild(this.barLayer, this.minimapView.sprite);

    this.overlays = new OverlayView(
      () => this.sim,
      this.camera,
      app.cache,
      () => app.uiScale,
      this.padLayer,
      this.bodyLayer,
      this.barLayer,
    );

    this.lastLayout = hudLayout(app.surface.width, app.surface.height, HOTBAR.length);
    this.paused = new URLSearchParams(location.search).has('paused');
    audio.setDepth(scenario.depth);
    this.nudges = new NudgeView(Tutor.armFor(app.profile, scenario.depth), {
      sim: this.sim,
      uiScale: () => app.uiScale,
      reducedMotion: () => app.reducedMotion,
      tileClient: (i, j) => this.tileClient(i, j),
      shoalClient: (id) => this.shoalClient(id),
      say: (text, ms) => this.say(text, ms),
      persist: () => void app.persist(),
    });
    this.applyCamera();

    if (opts.snapshot) {
      this.showBanner(S.run.backDown, `${this.sim.state.fightsCleared} FIGHTS CLEARED · THE OCEAN WAITED`);
    } else {
      this.showBanner(scenario.map.name, OPENING[scenario.depth] ?? OPENING[1]!);
      // a first-timer is about to be taught this by a card; the veteran is not
      if (!this.nudges.armed) this.say(S.run.clickShoalWorld, 6000);
    }
  }

  // -- the tutor seam (doc 19) -----------------------------------------------

  /**
   * The single funnel every player action goes through. While a gate is open
   * the tutor refuses to forward the command — it never edits one, and it never
   * touches the sim itself.
   */
  private send(cmd: Command): string | null {
    const refusal = this.nudges.allows(cmd);
    if (refusal) {
      this.say(refusal, 2600);
      audio.play('ui_deny');
      return refusal;
    }
    const err = this.sim.command(cmd);
    if (err) audio.play('ui_deny');
    else if (cmd.type === 'sell') audio.play('sell');
    return err;
  }

  // -- presentation helpers --------------------------------------------------

  say(text: string, ms = 2500): void {
    const now = performance.now();
    this.toast = { text, until: now + ms, since: now };
    this.hudDirty = true;
  }

  /** Doc 17 §6: in from the right over 140 ms, out over 120, integer px. */
  private toastSlide(now: number): number {
    const t = this.toast;
    if (!t || this.app.reducedMotion) return 0;
    const age = now - t.since;
    if (age < TOAST_IN_MS) return Math.round((1 - age / TOAST_IN_MS) * 26);
    const left = t.until - now;
    if (left < TOAST_OUT_MS) return Math.round((1 - left / TOAST_OUT_MS) * 26);
    return 0;
  }

  showBanner(title: string, sub: string, hold = false): void {
    this.banner = { title, sub, start: performance.now(), hold };
    this.hudDirty = true;
  }

  /**
   * A vertical gradient between the depth's two water colours, one pixel wide
   * and stretched: doc 10 §Palettes gives every depth a top and a bottom, and
   * this is the only place the player sees both at once.
   */
  private paintWater(): void {
    const h = Math.max(2, Math.ceil(this.app.viewport.cssH / 8));
    this.water.resize(1, h);
    const s = this.water.surface;
    for (let y = 0; y < h; y++) {
      s.px(0, y, mix(this.depth.waterTop, this.depth.waterBottom, y / (h - 1)), 'set');
    }
    this.water.upload();
    this.water.sprite.width = this.app.viewport.cssW;
    this.water.sprite.height = this.app.viewport.cssH;
  }

  private applyCamera(shake = { x: 0, y: 0 }): void {
    const st = this.app.stage;
    st.scale.set(this.camera.zoom);
    st.x = -this.camera.x * this.camera.zoom + shake.x;
    st.y = -this.camera.y * this.camera.zoom + shake.y;
  }

  // -- the meta seam (doc 08 / doc 12) ---------------------------------------

  bankClear(ev: Extract<SimEvent, { type: 'waveCleared' }>): void {
    const pearls = this.ledger.bankClear(ev);
    if (pearls === null) return;
    this.say(L.pearlsGained(pearls), 2600);
    this.snapshotRun();
  }

  private snapshotRun(): void {
    const err = this.ledger.snapshot();
    if (err) this.say(err, 4000);
  }

  finish(outcome: 'won' | 'lost'): void {
    if (this.finished) return;
    this.finished = true;
    this.opts.onExit(this.ledger.finish(outcome));
  }

  // -- sim events → presentation (doc 17 §4, §7) -----------------------------

  private onEvents(events: SimEvent[]): void {
    const now = performance.now();
    this.nudges.feed(events);
    // doc 18 §1: a banner-class event ducks the ambience while it is on screen
    if (playEvents(events, { camera: this.camera, viewport: this.app.viewport })) {
      audio.setDucked(true);
    }
    handleEvents(this, events, now);
    this.coreHp = watchCore(this, this.coreHp, now);
  }

  /**
   * Doc 06: a property gets a card the first time the player pulls something
   * that carries it — whatever order they pull in, and exactly once per colony.
   * At most one card per pull, because a shoal never teaches two new things.
   */
  introduceProperty(shoal: RimShoal): void {
    const profile = this.app.profile;
    const seen = profile?.propertiesSeen ?? this.propertiesSeen;
    for (const id of propertiesOf(shoal.composition)) {
      if (seen.includes(id)) continue;
      const card = PROPERTIES[id]!;
      seen.push(id);
      if (profile) this.app.persist();
      this.showBanner(`${card.marker} ${card.name}`, card.rule, true);
      this.say(card.counter, 7000);
      return;
    }
  }

  // -- picking ---------------------------------------------------------------

  private pointerTile(sx: number, sy: number): { i: number; j: number } {
    const w = this.camera.screenToWorld(sx, sy);
    const t = screenToTile(w.x, w.y);
    return { i: Math.floor(t.x), j: Math.floor(t.y) };
  }

  private pickStructure(sx: number, sy: number): Structure | null {
    const w = this.camera.screenToWorld(sx, sy);
    const ordered = [...this.sim.state.structures].sort(
      (a, b) => b.i + b.j + (b.size - 1) * 2 - (a.i + a.j + (a.size - 1) * 2),
    );
    for (const st of ordered) {
      const { r } = this.world.render(st, false, false);
      const c = blockCentre(st.i, st.j, st.size, st.size);
      const lx = Math.floor(w.x - (c.x - r.body.ax));
      const ly = Math.floor(w.y - (c.y - r.body.ay));
      if (r.body.surface.alphaAt(lx, ly) > 0) return st;
    }
    const t = this.pointerTile(sx, sy);
    if (!this.sim.grid.inBounds(t.i, t.j)) return null;
    const id = this.sim.grid.occ[this.sim.grid.idx(t.i, t.j)]!;
    return id >= 0 ? (this.sim.byId(id) ?? null) : null;
  }

  private selectShoal(shoal: RimShoal): void {
    this.selectedShoalId = shoal.id;
    this.selectedStructId = -1;
    this.say(S.run.spaceFlashLure, 2200);
    this.hudDirty = true;
  }

  // -- input -----------------------------------------------------------------

  onWheel(e: WheelEvent): void {
    e.preventDefault();
    this.camera.zoomAt(e.deltaY < 0 ? 1 : -1, e.offsetX, e.offsetY, this.app.viewport);
    this.applyCamera();
    this.hudDirty = true;
  }

  onMouseDown(e: MouseEvent): void {
    this.lastPointer = { x: e.clientX, y: e.clientY };
    this.nudges.noteInput(performance.now());
    if (e.button === 1) {
      this.dragging = true;
      e.preventDefault();
      return;
    }
    if (e.button === 2) {
      if (this.tool || this.aiming) {
        this.tool = null;
        this.aiming = null;
        this.hudDirty = true;
      } else {
        this.dragging = true;
      }
      e.preventDefault();
      return;
    }
    if (e.button !== 0) return;

    const { x: uiX, y: uiY } = this.app.ui(e);

    if (this.exitMenu) {
      const [yes, no] = this.exitMenu;
      if (hit(yes, uiX, uiY)) this.leave();
      else if (hit(no, uiX, uiY)) this.closeExitMenu();
      return;
    }

    // minimap first: it overlays the corner (doc 17 §3, click-to-jump)
    const mr = this.minimap.rect(this.app.surface.width, this.app.surface.height);
    if (uiX >= mr.x && uiX < mr.x + mr.w && uiY >= mr.y && uiY < mr.y + mr.h) {
      const shoal = this.minimap.shoalAt(this.sim, uiX - mr.x, uiY - mr.y);
      if (shoal) {
        this.selectShoal(shoal);
        return;
      }
      const w = this.minimap.worldFromMini(uiX - mr.x, uiY - mr.y);
      this.camera.centerOn(w.x, w.y, this.app.viewport);
      this.applyCamera();
      this.hudDirty = true;
      return;
    }

    if (this.nudges.dismiss(uiX, uiY)) {
      this.hudDirty = true;
      return;
    }
    const alert = this.alerts.hit(uiX, uiY);
    if (alert) {
      if (alert.at) {
        const c = blockCentre(alert.at.x, alert.at.y, 1, 1);
        this.camera.centerOn(c.x, c.y, this.app.viewport);
        this.applyCamera();
      }
      this.hudDirty = true;
      return;
    }

    if (speedHit(this.app.surface.width, uiX, uiY)) {
      this.fastForward = !this.fastForward;
      this.hudDirty = true;
      return;
    }

    const slot = abilityHit(this.lastLayout, uiX, uiY);
    if (slot >= 0) {
      this.arm((['q', 'w', 'e', 'r'] as const)[slot] ?? null);
      return;
    }
    const up = upgradeHit(this.lastLayout, uiX, uiY);
    if (up >= 0 && this.selectedStructId >= 0) {
      const path = (['a', 'b', 'c'] as const)[up];
      if (path) {
        const err = this.send({ type: 'upgrade', structureId: this.selectedStructId, path });
        if (err) this.say(err);
        else this.onEvents(this.sim.drainEvents()); // the flash starts now, not next tick
      }
      return;
    }
    const hitBtn = hotbarHit(this.lastLayout, uiX, uiY);
    if (hitBtn >= 0) {
      this.pick(BUILD_ORDER[hitBtn] ?? null);
      return;
    }

    if (this.aiming) {
      const w = this.camera.screenToWorld(e.offsetX, e.offsetY);
      const t = screenToTile(w.x, w.y);
      const err = this.send({ type: 'cast', ability: this.aiming, x: t.x, y: t.y });
      if (err) this.say(err);
      else {
        this.onEvents(this.sim.drainEvents());
        this.aiming = null;
      }
      return;
    }
    if (this.tool) {
      const t = this.pointerTile(e.offsetX, e.offsetY);
      const err = this.send({ type: 'place', def: this.tool, i: t.i, j: t.j });
      if (err) this.say(err);
      else this.onEvents(this.sim.drainEvents());
      this.hudDirty = true;
      return;
    }

    const shoal = this.rim.pick(this.camera, e.offsetX, e.offsetY);
    if (shoal) {
      this.selectShoal(shoal);
      return;
    }
    const st = this.pickStructure(e.offsetX, e.offsetY);
    this.selectedStructId = st ? st.id : -1;
    this.selectedShoalId = null;
    this.hudDirty = true;
  }

  onMouseUp(): void {
    this.dragging = false;
  }

  onMouseMove(e: MouseEvent): void {
    this.pointer = { x: e.offsetX, y: e.offsetY };
    if (this.dragging) {
      this.camera.panScreen(e.clientX - this.lastPointer.x, e.clientY - this.lastPointer.y, this.app.viewport);
      this.lastPointer = { x: e.clientX, y: e.clientY };
      this.applyCamera();
    }
    const { x: uiX, y: uiY } = this.app.ui(e);
    const btn = hotbarHit(this.lastLayout, uiX, uiY);
    if (btn !== this.hotbarHover) this.hotbarHover = btn;
    const ab = abilityHit(this.lastLayout, uiX, uiY);
    if (ab !== this.abilityHover) this.abilityHover = ab;
    const st = this.tool || this.aiming || btn >= 0 || ab >= 0 ? null : this.pickStructure(e.offsetX, e.offsetY);
    this.hoverStructId = st && (st.kind === 'tower' || st.def === 'ore_rig') ? st.id : -1;
    this.hudDirty = true;
  }

  /** Arm (or disarm) an ability for its click-to-aim (doc 07). */
  private arm(ability: AbilityId | null): void {
    if (ability === null) return;
    const allowed = this.sim.mods.allowedAbilities;
    if (allowed && !allowed.includes(ability)) {
      this.say(S.run.lockedBuyColony, 3000);
      return;
    }
    this.aiming = this.aiming === ability ? null : ability;
    this.tool = null;
    this.hudDirty = true;
  }

  private pick(def: string | null): void {
    this.tool = def;
    this.aiming = null;
    this.hudDirty = true;
    const allowed = this.sim.mods.allowedDefs;
    if (def && allowed && !allowed.includes(def)) {
      this.tool = null;
      this.say(S.run.lockedBuyColony, 3000);
    }
  }

  onKey(e: KeyboardEvent): void {
    const k = e.key.toLowerCase();
    this.keys.add(k);
    this.nudges.noteInput(performance.now());
    if (k === 'escape' && this.nudges.dismiss()) {
      this.hudDirty = true;
      return;
    }

    if (this.exitMenu) {
      if (k === 'arrowleft' || k === 'arrowright' || k === 'tab') this.exitFocus = this.exitFocus === 0 ? 1 : 0;
      if (k === 'enter') this.exitFocus === 0 ? this.leave() : this.closeExitMenu();
      if (k === 'escape') this.closeExitMenu();
      this.hudDirty = true;
      return;
    }

    if (e.key === ' ') {
      e.preventDefault();
      if (this.selectedShoalId) {
        const err = this.send({ type: 'pull', shoalId: this.selectedShoalId });
        if (err) this.say(err);
        else {
          this.onEvents(this.sim.drainEvents());
          this.selectedShoalId = null;
        }
      } else {
        this.say(S.run.selectShoalRim);
      }
    }
    if (k === 'q' || k === 'w' || k === 'e' || k === 'r') this.arm(k);
    if (k === 'p') this.paused = !this.paused;
    if (k === 'f') this.fastForward = !this.fastForward;
    if (k === 'l') {
      const circling = this.sim.state.rim.filter((x) => x.state === 'circling');
      if (circling.length > 0) {
        const at = circling.findIndex((x) => x.id === this.selectedShoalId);
        const next = circling[(at + 1) % circling.length]!;
        this.selectedShoalId = next.id;
        this.selectedStructId = -1;
        this.say(`${next.name} — SPACE PULLS IT`, 2200);
      }
    }
    if (!e.shiftKey && /^[0-9]$/.test(e.key)) {
      const idx = e.key === '0' ? 9 : Number(e.key) - 1;
      this.pick(BUILD_ORDER[idx] ?? null);
    }
    if (k === 'v') this.pick('gas_tap');
    if (k === 'b') this.pick('refinery');
    if (k === 'n') this.pick('generator');
    if (k === 'g') this.pick('power_pylon');
    if (k === 'h') this.pick('storage_depot');
    if (k === 'j') this.pick('repair_bay');
    if (k === 'escape') {
      if (this.tool || this.aiming) {
        this.tool = null;
        this.aiming = null;
      } else {
        this.openExitMenu();
      }
    }
    if (k === 'x' && this.selectedStructId >= 0) this.sell();
    if (k === 'c' || e.key === 'Home') {
      this.camera.centerOn(this.coreCentre.x, this.coreCentre.y, this.app.viewport);
      this.applyCamera();
    }
    for (let i = 0; i < ZOOM_LEVELS.length; i++) {
      if (e.key === String(i + 1) && e.shiftKey) {
        this.camera.setZoom(ZOOM_LEVELS[i]!, this.app.viewport);
        this.applyCamera();
      }
    }
    this.hudDirty = true;
  }

  onKeyUp(e: KeyboardEvent): void {
    this.keys.delete(e.key.toLowerCase());
  }

  onBlur(): void {
    this.paused = true;
    this.keys.clear();
    this.hudDirty = true;
  }

  /** Doc 18 §2: `confirm-before-sell` puts one keystroke between you and it. */
  private sell(): void {
    if (this.app.profile?.settings.confirmSell && this.sellPending !== this.selectedStructId) {
      this.sellPending = this.selectedStructId;
      this.say(S.run.xAgainSell, 2600);
      return;
    }
    const err = this.send({ type: 'sell', structureId: this.selectedStructId });
    if (err) this.say(err);
    else {
      this.selectedStructId = -1;
      this.sellPending = -1;
      this.say(S.run.soldNoRefunds);
    }
  }

  private openExitMenu(): void {
    this.paused = true;
    this.exitFocus = 1;
    this.exitMenu = [
      { x: 0, y: 0, w: 0, h: 0 },
      { x: 0, y: 0, w: 0, h: 0 },
    ];
    this.hudDirty = true;
  }

  private closeExitMenu(): void {
    this.exitMenu = null;
    this.paused = false;
    this.hudDirty = true;
  }

  /** Leave a live run: it stays in the save file exactly where it was. */
  private leave(): void {
    this.snapshotRun();
    this.ledger.file('left'); // doc 18 §7: where a run ended includes "walked out"
    this.app.persist();
    this.finished = true;
    this.opts.onExit(null);
  }

  // -- dev/test hooks (doc 13 §J: scripted browser tests use real events) ----

  /** Client-space centre of a tile, for scripted MouseEvents. */
  tileClient(i: number, j: number): { x: number; y: number } {
    return this.camera.worldToScreen((i - j) * TW, (i + j) * TH + TH);
  }

  shoalClient(id: string): { x: number; y: number } | null {
    const sh = this.sim.state.rim.find((x) => x.id === id);
    if (!sh || sh.state !== 'circling') return null;
    const p = this.rim.orbitToWorld(sh.orbit);
    return this.camera.worldToScreen(p.x, p.y);
  }

  resize(): void {
    this.paintWater();
    this.lastLayout = hudLayout(this.app.surface.width, this.app.surface.height, HOTBAR.length);
    this.camera.clamp(this.app.viewport);
    this.applyCamera();
    this.hudDirty = true;
  }

  destroy(): void {
    audio.leaveRun();
    this.app.layers.back.removeChild(this.water.sprite);
    this.water.sprite.destroy();
    this.ambience.destroy();
    this.numbers.clear();
    this.alerts.clear();
    this.app.layers.ui.removeChild(this.barLayer, this.minimapView.sprite);
    this.barLayer.destroy({ children: true });
    this.minimapView.sprite.destroy();
  }

  // -- frame -----------------------------------------------------------------

  private stepSim(): void {
    this.world.capturePrev();
    this.sim.tick();
    this.onEvents(this.sim.drainEvents());
    this.fx.step();
    this.hudDirty = true;
  }

  private bannerAlpha(now: number): number {
    if (!this.banner) return 0;
    const t = now - this.banner.start;
    // doc 18 §3: reduced motion means banners HOLD without sliding or stepping,
    // not that they vanish — the information is never the thing that is cut.
    if (this.app.reducedMotion) return this.banner.hold || t <= 2050 ? 1 : 0;
    if (this.banner.hold) return t < 250 ? Math.round((t / 250) * 4) / 4 : 1;
    if (t < 0 || t > 2050) return 0;
    const raw = t < 200 ? t / 200 : t < 1800 ? 1 : 1 - (t - 1800) / 250;
    return Math.round(Math.max(0, Math.min(1, raw)) * 4) / 4;
  }

  update(now: number): void {
    // The moment the run ends, this screen is replaced and torn down — every
    // exit below returns rather than drawing into destroyed sprites.
    if (this.finished) return;
    // safety net: the run is over the moment the sim says so, even if the
    // deciding event arrived outside a step (a restored save, a dev hook)
    if (this.sim.state.outcome !== 'playing') {
      this.onEvents(this.sim.drainEvents());
      if (!this.finished) this.finish(this.sim.state.outcome === 'won' ? 'won' : 'lost');
      return;
    }

    const settings = this.app.profile?.settings;
    this.juice.configure({
      shake: settings?.screenShake !== false,
      flash: (settings?.flash ?? 'full') as FlashChoice,
      reducedMotion: this.app.reducedMotion,
    });
    this.numbers.enabled = settings?.damageNumbers !== false;
    this.alerts.configure(this.app.reducedMotion);
    this.numbers.step(now);
    this.alerts.step(now);
    this.ambience.still = this.app.reducedMotion;
    this.ambience.step(now);

    // doc 17 §7: hit-stop withholds FRAMES, never ticks. The 40 ms is paid back
    // by the accumulator on the very next frame, so the sim cannot drift.
    if (!this.paused && !this.juice.frozen(now)) {
      const dt = Math.min(0.25, this.app.layers.app.ticker.deltaMS / 1000);
      this.acc += dt * (this.fastForward ? 2 : 1);
      let steps = 0;
      while (this.acc >= DT && steps < 6) {
        this.stepSim();
        this.acc -= DT;
        steps++;
        if (this.finished) return; // victory or defeat landed mid-step
      }
      if (steps === 6) this.acc = 0; // long stall: drop time rather than spiral
    }

    if (this.nudges.teach(now, this.selectedStructId)) this.hudDirty = true;

    const speed = 4;
    let dx = 0;
    let dy = 0;
    if (this.keys.has('a') || this.keys.has('arrowleft')) dx -= speed;
    if (this.keys.has('d') || this.keys.has('arrowright')) dx += speed;
    if (this.keys.has('w') || this.keys.has('arrowup')) dy -= speed;
    if (this.keys.has('s') || this.keys.has('arrowdown')) dy += speed;
    // doc 17 §2: 8 px margin, toggleable. The bottom band is the hotbar's, so
    // resting the pointer on a build button never drags the camera with it.
    if (this.app.profile?.settings.edgeScroll !== false && this.pointer.x >= 0 && !this.exitMenu) {
      const { cssW, cssH } = this.app.viewport;
      const hud = this.lastLayout.hotbar[0]?.y;
      const floor = hud !== undefined ? hud * this.app.uiScale : cssH;
      if (this.pointer.y < floor) {
        if (this.pointer.x < EDGE_MARGIN) dx -= speed;
        if (this.pointer.x > cssW - EDGE_MARGIN) dx += speed;
        if (this.pointer.y < EDGE_MARGIN) dy -= speed;
        if (this.pointer.y > floor - EDGE_MARGIN) dy += speed;
      }
    }
    if (dx !== 0 || dy !== 0) {
      this.camera.panWorld(dx, dy, this.app.viewport);
      this.applyCamera();
      this.hudDirty = true;
    }

    // doc 17 §7: the shake moves the WORLD, never the interface
    const shake = this.juice.offset(now);
    if (shake.x !== 0 || shake.y !== 0 || this.shaking) {
      this.shaking = shake.x !== 0 || shake.y !== 0;
      this.applyCamera(shake);
    }

    const alpha = Math.max(0, Math.min(1, this.acc / DT));
    this.world.syncStructures(this.hoverStructId, this.selectedStructId);
    this.world.syncEnemies(alpha);
    this.rim.sync(this.selectedShoalId);
    this.overlays.sync({
      selectedStructId: this.selectedStructId,
      tool: this.tool,
      aiming: this.aiming,
      pointer: this.pointer,
      alwaysRanges: this.alwaysRanges,
      world: this.world,
      fx: this.fx,
      aimRadius: this.aiming ? aimRadius(this.aiming) : 0,
      pointerTile: (sx, sy) => this.pointerTile(sx, sy),
    });
    if (this.overlays.lostSelection) {
      this.selectedStructId = -1;
      this.overlays.lostSelection = false;
    }
    this.overlays.bars();

    audio.setCombat(this.sim.phase === 'combat');
    // doc 17 §7: an empty magazine clicks rather than firing. Once every two
    // seconds is a reminder; once per dry shot would be the audio wall again.
    if (this.sim.phase === 'combat' && this.sim.state.resources.ammo < 1 && now - this.lastDry > 2000) {
      this.lastDry = now;
      audio.play('dry');
    }
    // the duck lifts with the banner it was raised for
    if (this.banner === null || this.bannerAlpha(now) === 0) audio.setDucked(false);

    if (this.hudDirty && now - this.lastHud > 66) {
      this.drawHud(now);
      this.lastHud = now;
      this.hudDirty = false;
    }
    if (this.banner && this.bannerAlpha(now) > 0) this.hudDirty = true;
  }

  private drawHud(now: number): void {
    const t = this.pointerTile(this.pointer.x, this.pointer.y);
    const alphaB = this.bannerAlpha(now);
    const b: Banner | null =
      this.banner && alphaB > 0
        ? { title: this.banner.title, sub: this.banner.sub, alpha: alphaB }
        : null;
    const panic = this.sim.phase === 'combat' ? ` (+${Math.round(PANIC_TAX * 100)}% PANIC TAX)` : '';
    const surface = this.app.surface;
    this.lastLayout = drawHud(
      surface,
      buildHudState({
        sim: this.sim,
        depth: this.depth,
        selectedStructId: this.selectedStructId,
        selectedShoalId: this.selectedShoalId,
        tool: this.tool,
        hotbarHover: this.hotbarHover,
        abilityHover: this.abilityHover,
        aiming: this.aiming,
        exactCounts: this.exactCounts,
        banner: b,
        fast: this.fastForward,
        toast: toastLine(
          this.toast && now < this.toast.until
            ? this.toast.text
            : this.exitMenu
              ? null
              : this.paused
                ? S.run.pausedPResume
                : this.aiming
                  ? `AIMING ${abilitiesJson.abilities[this.aiming].name} — CLICK TO CAST, RIGHT-CLICK CANCELS`
                  : this.tool
                    ? `PLACING ${placeableDef(this.tool)!.name}${panic} — RIGHT-CLICK CANCELS`
                    : null,
          this.toastSlide(now),
        ),
        debug: `TICK ${this.sim.state.tick} · ACT ${this.sim.state.act} · ${this.sim.phase.toUpperCase()} · ZOOM ${this.camera.zoom}X · UI ${this.app.uiScale}X · TILE ${t.i},${t.j} · ${this.sim.state.enemies.length} FISH · ${this.fx.count} FX`,
      }),
    );
    // damage numbers ride the world but are drawn in UI space, so they stay
    // crisp at every zoom (doc 17 §1: two resolutions, one of them for text)
    const u = this.app.uiScale;
    this.numbers.draw(surface, now, (x, y) => {
      const p = this.camera.worldToScreen(Math.round((x - y) * TW), Math.round((x + y) * TH + TH) - 14);
      const ui = { x: p.x / u, y: p.y / u };
      return ui.x < -20 || ui.y < -20 || ui.x > surface.width + 20 || ui.y > surface.height + 20 ? null : ui;
    });
    this.alerts.draw(surface, now, { x: surface.width - g(60) - g(3), y: g(17), w: g(60) });

    if (this.nudges.draw(surface, now, this.lastLayout.hotbar[0]?.y ?? surface.height)) {
      this.hudDirty = true; // the pulse blinks: keep repainting while it is up
    }

    if (this.exitMenu) {
      this.exitMenu = drawConfirm(
        surface,
        S.run.leaveColony,
        S.run.runSavedLast,
        [S.run.saveLeave, S.run.keepFighting],
        this.exitFocus,
      );
    }
    this.app.hud.upload();

    this.minimapView.sprite.visible = this.app.profile?.settings.minimap !== false;
    if (!this.minimapView.sprite.visible) return;
    this.minimap.redraw(
      this.sim,
      this.camera,
      this.app.viewport,
      this.selectedShoalId,
      this.nudges.minimapPulse(now),
      this.alerts.pings(),
    );
    this.minimapView.surface.clear();
    this.minimapView.surface.blit(this.minimap.surface, 0, 0, 'set');
    this.minimapView.upload();
    const mr = this.minimap.rect(surface.width, surface.height);
    this.minimapView.sprite.x = mr.x;
    this.minimapView.sprite.y = mr.y;
  }
}

/** What the reticle should preview for each ability (doc 07 radii). */
function aimRadius(ability: AbilityId): number {
  const a = abilitiesJson.abilities;
  if (ability === 'q') return a.q.targetRadius;
  if (ability === 'w') return a.w.radius;
  if (ability === 'e') return a.e.radius;
  return a.r.laneWidth;
}

/** Null stays null; a line gains its slide (doc 17 §6). */
function toastLine(text: string | null, slide: number): { text: string; slide: number } | null {
  return text === null ? null : { text, slide };
}

function hit(r: Rect, x: number, y: number): boolean {
  return x >= r.x && y >= r.y && x < r.x + r.w && y < r.y + r.h;
}
